import type { DateToString } from '@/types/util/dataUtil/dateToString'

/**
 * ISO8601文字列をDateオブジェクトに変換する（デシリアライズ）関数
 * @param isoString ISO8601形式の文字列（例: "2026-01-12T04:00:00+00:00"）
 * @returns {Date} 変換したDateオブジェクト
 */
export function deserializeDateFromIsoString(isoString: string): Date {
  return new Date(isoString)
}

/**
 * 日付をフォーマット
 * @param date
 * @param formatStr
 * @returns {string} フォーマットされた日付文字列
 */
export function formatDate(date?: Date | string, formatStr = 'YYYY/MM/DD') {
  if (!date) return ''

  if (typeof date === 'string') {
    date = new Date(date)
  }

  const options: Intl.DateTimeFormatOptions = {}

  if (formatStr.includes('YYYY')) {
    options.year = 'numeric'
  }
  if (formatStr.includes('MM')) {
    options.month = '2-digit'
  }
  if (formatStr.includes('DD')) {
    options.day = '2-digit'
  }

  return date.toLocaleDateString('ja-JA', options)
}

export function jaFormatDate(date?: Date | string, formatStr = 'YYYY年MM月DD日') {
  if (!date) return ''

  if (typeof date === 'string') {
    date = new Date(date)
  }

  const year = date.getFullYear()
  const month = ('0' + (date.getMonth() + 1)).slice(-2)
  const day = ('0' + date.getDate()).slice(-2)

  return formatStr.replace(/YYYY/g, year.toString()).replace(/MM/g, month).replace(/DD/g, day)
}

export const toISODateString = (date: Date | string) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }
  return date.toISOString().split('T')[0]
}

export const toISODatetimeString = (date: Date | string) => {
  if (typeof date === 'string') {
    date = new Date(date)
  }
  return date.toISOString()
}

export const formatDatetime = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short'
  }

  const dateString = date.toLocaleDateString('ja-JP', options)

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${dateString} ${hours}:${minutes}`
}

type JsonValue = string | number | boolean | null | JsonObject | Array<JsonValue> | Date
interface JsonObject {
  [key: string]: JsonValue
}

export function convertIsoStringsToDate<T>(value: DateToString<T>): T | Date {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?[Zz]?/

  if (Array.isArray(value)) {
    return value.map(item => convertIsoStringsToDate(item)) as T
  } else if (typeof value === 'object' && value !== null) {
    const newObj: JsonObject = {}
    for (const [k, v] of Object.entries(value)) {
      const _value = v as JsonValue
      newObj[k] =
        typeof _value === 'string' && isoDateRegex.test(_value)
          ? new Date(_value)
          : convertIsoStringsToDate<DateToString<JsonValue>>(_value)
    }
    return newObj as T
  }

  return typeof value === 'string' && isoDateRegex.test(value) ? new Date(value) : value
}

export function convertDatesToIsoString<T>(value: T): DateToString<T> | string {
  if (Array.isArray(value)) {
    return value.map(item => convertDatesToIsoString(item)) as DateToString<T>
  } else if (typeof value === 'object' && value !== null) {
    const result: JsonObject = {}
    for (const [k, v] of Object.entries(value)) {
      const _value = v as JsonValue
      if (value.hasOwnProperty(k)) {
        result[k] = _value instanceof Date ? _value.toISOString() : convertDatesToIsoString(_value)
      }
    }
    return result as DateToString<T>
  }

  return value instanceof Date ? value.toISOString() : (value as DateToString<T>)
}

// エポックミリ秒を比較する
export const compareDates = (date1: number, date2: number): number => {
  if (date1 < date2) {
    return -1
  } else if (date1 > date2) {
    return 1
  } else {
    return 0
  }
}

export function jaFormatDateTime(date?: Date | string, formatStr = 'YYYY年MM月DD日 HH:mm') {
  if (!date) return ''

  if (typeof date === 'string') {
    date = new Date(date)
  }

  const year = date.getFullYear()
  const month = ('0' + (date.getMonth() + 1)).slice(-2)
  const day = ('0' + date.getDate()).slice(-2)
  const hours = ('0' + date.getHours()).slice(-2)
  const minutes = ('0' + date.getMinutes()).slice(-2)

  return formatStr
    .replace(/YYYY/g, year.toString())
    .replace(/MM/g, month)
    .replace(/DD/g, day)
    .replace(/HH/g, hours)
    .replace(/mm/g, minutes)
}

/**
 * Base64データをArrayBufferに変換する
 * @param base64
 * @returns
 */
export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const base64Data = base64.includes(',') ? base64.split(',')[1] : base64
  const binaryString = atob(base64Data)
  const len = binaryString.length
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}
